import { useState } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { Button } from '@/shared/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { t } from '@/shared/lib/i18n'
import {
  adminCodeValuePath,
  adminCodeValuesPath,
  editAdminCodeValuePath,
  newAdminCodeValuePath,
} from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { useCan } from '@/shared/hooks/useCan'
import { Resources, Actions } from '@/shared/rbac'
import type { SharedProps } from '@/shared/types/SharedProps'
import type { WebAdminCodeValue } from '@/shared/types/web'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'
import { formatLookupCategoryLabel } from '@/shared/lib/formatLookupCategoryLabel'

interface CodeValuesIndexPageProps {
  codeValues: WebAdminCodeValue[]
  categories: string[]
  filters: { category: string }
  pagination: PaginationMeta
}

type PageProps = SharedProps & CodeValuesIndexPageProps

const ALL_CATEGORIES_SENTINEL = '__all__'

const CodeValuesIndexPage = () => {
  const { codeValues, categories, filters, pagination } = usePage<PageProps>().props

  useBreadcrumbs([{ label: t('nav.administration') }, { label: t('nav.codeValues') }])

  const canCreate = useCan(Resources.CODE_VALUES, Actions.CREATE)
  const canUpdate = useCan(Resources.CODE_VALUES, Actions.UPDATE)
  const canDestroy = useCan(Resources.CODE_VALUES, Actions.DELETE)

  const [category, setCategory] = useState(
    filters.category ? filters.category : ALL_CATEGORIES_SENTINEL,
  )

  const applyCategoryFilter = () => {
    const query = category && category !== ALL_CATEGORIES_SENTINEL ? { category } : {}
    router.visit(adminCodeValuesPath(query), { preserveState: true })
  }

  const goToPage = (pageNumber: number) => {
    const query: Record<string, string | number> = {
      page: pageNumber,
      limit: pagination.limit,
    }
    if (filters.category) {
      query.category = filters.category
    }
    router.visit(adminCodeValuesPath(query), { preserveState: true })
  }

  const handleDelete = (id: number) => {
    if (!window.confirm(t('admin.codeValues.deleteConfirm'))) return
    router.delete(adminCodeValuePath(id), { preserveScroll: true })
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold">{t('admin.codeValues.pageTitle')}</h1>
        {canCreate && (
          <Button asChild>
            <Link href={newAdminCodeValuePath()}>{t('admin.codeValues.newButton')}</Link>
          </Button>
        )}
      </div>

      <div className="space-y-3 rounded-md border p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[12rem] flex-1 space-y-1.5">
            <span className="text-sm font-medium text-muted-foreground">
              {t('admin.codeValues.categoryFilter')}
            </span>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger>
                <SelectValue placeholder={t('admin.codeValues.allCategories')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CATEGORIES_SENTINEL}>
                  {t('admin.codeValues.allCategories')}
                </SelectItem>
                {categories.map((categoryOption) => (
                  <SelectItem key={categoryOption} value={categoryOption}>
                    {formatLookupCategoryLabel(categoryOption)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button type="button" onClick={applyCategoryFilter}>
            {t('admin.codeValues.applyFilter')}
          </Button>
        </div>
      </div>

      {/* Mobile: card list */}
      <div className="space-y-2 lg:hidden">
        {codeValues.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">{t('admin.codeValues.emptyState')}</p>
          </div>
        ) : (
          codeValues.map((row) => (
            <div key={row.id} className="rounded-lg border bg-card p-3 shadow-sm space-y-2">
              <p className="font-semibold font-mono">{row.code}</p>
              {row.description && (
                <p className="text-sm text-muted-foreground">{row.description}</p>
              )}
              <div className="flex items-center gap-3 text-xs text-muted-foreground">
                <span>
                  {t('admin.codeValues.columns.category')}:{' '}
                  {row.category ? formatLookupCategoryLabel(row.category) : '—'}
                </span>
                <span>
                  {t('admin.codeValues.columns.sortOrder')}: {row.sortOrder ?? '—'}
                </span>
              </div>
              {(canUpdate || canDestroy) && (
                <div className="flex gap-2">
                  {canUpdate && (
                    <Button variant="outline" size="sm" asChild>
                      <Link href={editAdminCodeValuePath(row.id)}>
                        {t('admin.codeValues.editButton')}
                      </Link>
                    </Button>
                  )}
                  {canDestroy && (
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(row.id)}
                    >
                      {t('admin.codeValues.deleteButton')}
                    </Button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden rounded-md border lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.codeValues.columns.code')}</TableHead>
              <TableHead>{t('admin.codeValues.columns.description')}</TableHead>
              <TableHead>{t('admin.codeValues.columns.category')}</TableHead>
              <TableHead className="text-right">
                {t('admin.codeValues.columns.sortOrder')}
              </TableHead>
              <TableHead className="w-[1%] text-right">
                {t('admin.codeValues.columns.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {codeValues.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center text-muted-foreground">
                  {t('admin.codeValues.emptyState')}
                </TableCell>
              </TableRow>
            ) : (
              codeValues.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">{row.code}</TableCell>
                  <TableCell>{row.description ?? '—'}</TableCell>
                  <TableCell>
                    {row.category ? formatLookupCategoryLabel(row.category) : '—'}
                  </TableCell>
                  <TableCell className="text-right">{row.sortOrder ?? '—'}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {canUpdate && (
                        <Button variant="outline" size="sm" asChild>
                          <Link href={editAdminCodeValuePath(row.id)}>
                            {t('admin.codeValues.editButton')}
                          </Link>
                        </Button>
                      )}
                      {canDestroy && (
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => handleDelete(row.id)}
                        >
                          {t('admin.codeValues.deleteButton')}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination.totalPages > 1 && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <span>
            {t('admin.codeValues.paginationPage')} {pagination.page}{' '}
            {t('admin.codeValues.paginationOf')} {pagination.totalPages} ({pagination.total}{' '}
            {t('admin.codeValues.paginationNoun')})
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pagination.page <= 1}
              onClick={() => goToPage(pagination.page - 1)}
            >
              {t('admin.codeValues.previous')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => goToPage(pagination.page + 1)}
            >
              {t('admin.codeValues.next')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default CodeValuesIndexPage
